import { getMixpanelClient } from '@Src/mfe.config';
import { useDashboardStore } from '@Stores/dashboard.store';
import {
    EVENT,
    INITIATION_SOURCE,
    PURPOSE_TRACKING_VALUES,
    UNKNOWN_PURPOSE_TYPE,
} from '@Constants/tracking.constants';
import type { OtherPaymentType } from '@Constants/otherPaymentType.constants';

/**
 * Entry-point tracking for a payment the participant starts themselves.
 *
 * Every event carries Initiation source so these can be told apart from the
 * task-backed payments payments-mfe already reports, without editing an event
 * an existing board reads.
 */
const basePayload = () => {
    const deal = useDashboardStore().activeDeal;
    return {
        'Request Type': 'Payments',
        'Account Id': deal?.company?.id ?? null,
        'Account Name': deal?.company?.name ?? null,
        'Initiation source': INITIATION_SOURCE,
    };
};

const purposeType = (purpose: string) => PURPOSE_TRACKING_VALUES[purpose] ?? UNKNOWN_PURPOSE_TYPE;

/**
 * The offer was rendered. The denominator for everything below, and the only way
 * to tell "nobody wanted this" apart from "nobody was shown it".
 */
export const trackEntryViewed = (purposes: string[]) => {
    getMixpanelClient()?.track(EVENT.ParticipantPaymentEntryViewed, {
        ...basePayload(),
        'Purposes offered': purposes.map(purposeType),
    });
};

export const trackPurposeSheetOpened = () => {
    getMixpanelClient()?.track(EVENT.ParticipantPaymentPurposeSheetOpened, basePayload());
};

// The enum name, not the free-text description, so no participant-written text reaches analytics.
const otherTypeProperty = (otherType?: OtherPaymentType) =>
    otherType ? { 'Other payment type': otherType } : {};

export const trackPurposeSelected = (purpose: string, otherType?: OtherPaymentType) => {
    getMixpanelClient()?.track(EVENT.ParticipantPaymentPurposeSelected, {
        ...basePayload(),
        'Purpose type': purposeType(purpose),
        ...otherTypeProperty(otherType),
    });
};

/**
 * The bootstrap refused or failed. Without this the drop-off between a purpose
 * being selected and payments-mfe reporting Client Payment Started is invisible,
 * and a purpose the BFF has stopped allowing looks like abandonment.
 */
export const trackStartFailed = (purpose: string, otherType?: OtherPaymentType) => {
    getMixpanelClient()?.track(EVENT.ParticipantPaymentStartFailed, {
        ...basePayload(),
        'Purpose type': purposeType(purpose),
        ...otherTypeProperty(otherType),
    });
};
